import useChallengesQuery from "@hooks/common/challenges/use-challenges-query";
import ChallengeStatus from "@Enums/ChallengeStatus";
import useGlobalContext from "@hooks/common/general/use-global-context";
import useDemoAccount from "@hooks/common/user/use-demo-account";
import useI18n from "@i18n";
import { Box, Skeleton, useTheme } from "@mui/material";
import { ChallengeResponse } from "@Shared/schemas/challenges/challengeSchemas";
import { isNumber } from "lodash-es";
import { useEffect, useState } from "react";
import { Autoplay } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { BybStepper } from "../common/styled-components";
import HeroBannerCard from "./hero-banner-card";
import HeroBannerFallback from "./hero-banner-fallback";

const HeroBannerSection = () => {
  const { globalState } = useGlobalContext();
  const { i18n } = useI18n();
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [swiper, setSwiper] = useState<any>();
  const [bannerChallenges, setBannerChallenges] = useState<any[]>([]);
  const [challengesResolved, setChallengesResolved] = useState(false);
  const maxSteps = bannerChallenges?.length || 0;
  const { isDemo } = useDemoAccount();

  const { challenges: promoChallenges, isLoading: promoChallengesLoading } = useChallengesQuery(
    {
      include_tags: ["MONTHLY_CHALLENGE"],
      include_fields: ["scenarios", "targets", "rewards"],
      order_by: [
        {
          field: "created_at",
          direction: "desc",
        },
      ],
    },
    { enabled: !isDemo }
  );

  const { challenges: latestChallenges, isLoading: latestChallengesLoading } = useChallengesQuery(
    {
      limit: 3,
      filters: { max_rating: globalState.user.userRating! },
      include_fields: ["scenarios", "targets", "rewards"],
      order_by: [
        {
          field: "created_at",
          direction: "desc",
        },
      ],
    },
    { enabled: !isDemo }
  );

  const hasPromoChallenges = promoChallenges && promoChallenges.length > 0;
  const hasLatestChallenges = latestChallenges && latestChallenges.length > 0;

  useEffect(() => {
    swiper?.on("activeIndexChange", function ({ realIndex }: any) {
      setActiveStep(realIndex);
    });
  }, [swiper]);

  useEffect(() => {
    if (!promoChallengesLoading && !latestChallengesLoading) {
      let bannerChallenges_: any[] = [];
      if (hasPromoChallenges) {
        const challenge =
          promoChallenges.find((c: ChallengeResponse) => c.status === null || c.status === ChallengeStatus.NOT_STARTED) ||
          promoChallenges[promoChallenges.length - 1];
        bannerChallenges_ = [challenge];
      } else if (hasPromoChallenges) {
        bannerChallenges_ = promoChallenges.filter(
          (c: ChallengeResponse) => (!c.start_at || new Date(c.start_at) <= new Date()) && (!c.end_at || new Date(c.end_at) >= new Date())
        );
      }

      if (bannerChallenges_.length === 0 && hasLatestChallenges) {
        const unlockedChallenges = latestChallenges.filter(
          (c: ChallengeResponse) => !c.unlocked_at || !isNumber(globalState.user.userRating) || c.unlocked_at <= globalState.user.userRating
        );
        bannerChallenges_ = unlockedChallenges.length > 0 ? [unlockedChallenges[0]] : [];
      }

      setBannerChallenges(bannerChallenges_);
      setChallengesResolved(true);
    }
  }, [promoChallenges, latestChallenges, hasPromoChallenges, hasPromoChallenges, promoChallengesLoading, latestChallengesLoading]);

  return (
    <Box sx={{ width: "100%", height: theme.spacing(73.25), position: "relative", isolation: "isolate" }}>
      {promoChallengesLoading || latestChallengesLoading || !challengesResolved ? (
        <Skeleton variant="rectangular" width="100%" height="100%" />
      ) : (
        <>
          <Swiper
            onSwiper={setSwiper}
            initialSlide={0}
            style={{ height: "100%" }}
            loop={maxSteps > 1}
            autoplay={{
              delay: 10000,
              disableOnInteraction: false,
            }}
            modules={[Autoplay]}
          >
            {bannerChallenges && bannerChallenges.length > 0 ? (
              bannerChallenges
                .filter(challenge => !!challenge)
                .map((challenge, index) => {
                  return (
                    <SwiperSlide key={index} style={{ cursor: "pointer", height: "100%" }}>
                      {Math.abs(activeStep - index) <= 2 ? (
                        <HeroBannerCard challenge={challenge} challengeText={i18n(`challenges.${hasPromoChallenges ? "promo" : "latest"}.caption`)} />
                      ) : null}
                    </SwiperSlide>
                  );
                })
            ) : (
              <SwiperSlide key={0} style={{ cursor: "pointer", height: "100%" }}>
                <HeroBannerFallback />
              </SwiperSlide>
            )}
          </Swiper>
          {maxSteps > 1 && <BybStepper steps={maxSteps} position="static" activeStep={activeStep} nextButton={null} backButton={null} />}
        </>
      )}
    </Box>
  );
};

export default HeroBannerSection;
